import * as m from '@staiter/i18n';
import * as tx from '@staiter/i18n';
/**
 * FM-S-multi-4-13 — /admin/entities list page.
 */
import { useQueryClient } from '@tanstack/react-query';
import { Link, createFileRoute } from '@tanstack/react-router';
import { Building, Plus } from 'lucide-react';
import { useState } from 'react';
import { PageHeader, Table } from '~/components/data';
import { emitToast } from '~/components/feedback';
import { Button, Dialog, Input, Select } from '~/components/primitives';
import { useCreateEntity, useEntities, useHoldings } from '~/lib/api/tenancy';

export const Route = createFileRoute('/_auth/admin/entities')({ component: EntitiesAdmin });

function EntitiesAdmin(): JSX.Element {
  const { data, isLoading } = useEntities();
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <div>
      <PageHeader
        title={m.admin_entities_title()}
        description={m.admin_entities_subtitle()}
        actions={
          <Button onClick={() => setCreateOpen(true)} leadingIcon={<Plus size={14} />}>
            {tx.ui_sozdat_kompaniyu()}
          </Button>
        }
      />

      {isLoading ? (
        <p>{tx.ui_zagruzka()}</p>
      ) : (
        <Table
          ariaLabel={m.admin_entities_title()}
          data={data?.items ?? []}
          rowKey={(e) => e.id}
          columns={[
            {
              id: 'name',
              header: m.admin_col_name(),
              accessor: (e) => (
                <Link
                  to="/admin/entities/$entityId"
                  params={{ entityId: e.id }}
                  style={{ color: 'var(--text-link)', textDecoration: 'none' }}
                >
                  <Building size={14} style={{ marginRight: 6, verticalAlign: 'middle' }} />
                  {e.name}
                </Link>
              ),
            },
            {
              id: 'holding',
              header: m.admin_entities_holding(),
              accessor: (e) => e.holding?.name ?? '—',
            },
            { id: 'bin', header: m.admin_entities_bin(), accessor: (e) => e.bin ?? '—' },
            {
              id: 'currency',
              header: m.admin_entities_currency(),
              accessor: (e) => e.default_currency,
            },
            { id: 'status', header: m.admin_col_status(), accessor: (e) => e.status },
          ]}
          emptyTitle={m.admin_entities_empty()}
        />
      )}

      {createOpen && <CreateEntityDialog onClose={() => setCreateOpen(false)} />}
    </div>
  );
}

function CreateEntityDialog({ onClose }: { onClose: () => void }): JSX.Element {
  const [name, setName] = useState('');
  const [holdingId, setHoldingId] = useState<string>('');
  const [bin, setBin] = useState('');
  const [currency, setCurrency] = useState<'KZT' | 'USD' | 'EUR' | 'RUB'>('KZT');
  const holdings = useHoldings();
  const qc = useQueryClient();
  const createMut = useCreateEntity();

  return (
    <Dialog open onClose={onClose} title={tx.ui_sozdat_kompaniyu()}>
      <form
        onSubmit={async (e) => {
          e.preventDefault();
          try {
            await createMut.mutateAsync({
              name: name.trim(),
              holding_id: holdingId || null,
              bin: bin.trim() || null,
              default_currency: currency,
            });
            emitToast({ variant: 'success', title: m.admin_entities_created() });
            await qc.invalidateQueries({ queryKey: ['admin', 'entities'] });
            onClose();
          } catch {
            emitToast({ variant: 'error', title: m.admin_entities_create_failed() });
          }
        }}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <Input
            label={m.admin_col_name()}
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus
          />
          <Select
            label={m.admin_entities_holding_optional()}
            value={holdingId}
            onChange={(e) => setHoldingId(e.target.value)}
            options={[
              { value: '', label: m.entity_switcher_no_holding() },
              ...(holdings.data?.items ?? []).map((h) => ({ value: h.id, label: h.name })),
            ]}
          />
          <Input
            label={m.admin_entities_bin_optional()}
            value={bin}
            onChange={(e) => setBin(e.target.value)}
            placeholder={m.admin_entities_bin_hint()}
          />
          <Select
            label={m.admin_entities_currency_default()}
            value={currency}
            onChange={(e) => setCurrency(e.target.value as 'KZT' | 'USD' | 'EUR' | 'RUB')}
            options={[
              { value: 'KZT', label: 'KZT' },
              { value: 'USD', label: 'USD' },
              { value: 'EUR', label: 'EUR' },
              { value: 'RUB', label: 'RUB' },
            ]}
          />
        </div>
        <div style={{ marginTop: 16, display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button type="button" variant="ghost" onClick={onClose}>
            {tx.ui_otmena_2()}
          </Button>
          <Button type="submit" disabled={createMut.isPending}>
            {tx.ui_sozdat()}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
